/*
  KP: VARIANT MATCH across the website.

  When the shopper has a phone case in the cart (for example a Samsung Galaxy S25 case),
  products everywhere on the site are shown for that same phone:
    - product tiles (collection pages, search, any grid of the theme's own tiles):
      photo, link and quick add switch to that model's variant, and the price reads
      "from" and the cheapest price that model comes in;
    - "You may also like" and "Recently viewed" rows (assets/kp-recs.js,
      assets/kp-recent.js): photo, link and price, the same way;
    - product pages open on that model, unless the address already names a variant;
    - collection pages with the model dropdown (assets/kp-case-filters.js) start on
      that model when the shopper has not chosen one there in this visit.
  Rebuy's own rows (inside the Rebuy cart) already do this themselves and are not touched.

  Which phone:
    - the newest phone case in the cart (the "Model" of the newest cart line that has one);
    - or, if the shopper has since chosen a model in the collection page dropdown, that
      one (whichever happened last wins);
    - nothing in the cart and nothing chosen: nothing changes anywhere.
  Products that do not come in that model (prints, homeware...) are left as they are.

  The cart is checked when a page opens and again whenever the Rebuy cart or our own
  scripts say it has changed. The answer is kept for the visit (sessionStorage
  "kpCartModel") so the next page can use it straight away.

  Other scripts can use window.kpVariantMatch:
    kpVariantMatch.get()     the model now ('' for none)
    kpVariantMatch.ready     a promise of the model once the cart has been checked
                             (straight away when this visit already knows it)
  and the page gets a "kp:model" event (detail: { model }) when it changes.

  Loaded on every page by snippets/kp-extras.liquid (part 12). Delete that line to switch it off.
*/
(function () {
  'use strict';

  var CART_KEY = 'kpCartModel';
  var CHOICE_KEY = 'kpCaseChoice';
  var API = '/api/2025-07/graphql.json';
  var TYPES = ['Tough Case', 'Snap Case', 'Magsafe Tough Case'];
  var BATCH = 20;
  var MONEY_TOKEN = /\{\{\s*(\w+)\s*\}\}/;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function readJson(key) {
    try {
      return JSON.parse(window.sessionStorage.getItem(key) || 'null');
    } catch (e) {
      return null;
    }
  }

  function writeJson(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }

  // ---- Which model ----

  function cartRecord() {
    var saved = readJson(CART_KEY);
    return saved && typeof saved.model === 'string' ? saved : null;
  }

  function choiceRecord() {
    var saved = readJson(CHOICE_KEY);
    return saved && typeof saved.model === 'string' && saved.model ? saved : null;
  }

  function current() {
    var cart = cartRecord();
    var choice = choiceRecord();

    if (choice && (!cart || !cart.model || (choice.t || 0) > (cart.t || 0))) return choice.model;
    return cart ? cart.model : '';
  }

  // Newest cart line first (that is the order the cart comes back in). AirPods and iPad
  // cases have a "Model" option too ("Airpod Pro Gen2", "iPad Air 11..."): those are not
  // phones, so they are passed over.
  var NOT_A_PHONE = /^(airpod|ipad)/i;

  function modelInCart(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var options = items[i].options_with_values || [];
      for (var k = 0; k < options.length; k++) {
        if (options[k].name === 'Model' && options[k].value && !NOT_A_PHONE.test(options[k].value)) return options[k].value;
      }
    }
    return '';
  }

  var shownModel = null;
  var resolveReady;
  var ready = new Promise(function (resolve) {
    resolveReady = resolve;
  });

  // Already known in this visit: use it at once (the cart check below corrects it).
  if (cartRecord()) resolveReady(current());

  function announce() {
    var model = current();
    if (model === shownModel) return;
    shownModel = model;
    paintAll();
    try {
      document.dispatchEvent(new CustomEvent('kp:model', { detail: { model: model } }));
    } catch (e) {}
  }

  function checkCart() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var model = modelInCart(cart);
        var saved = cartRecord();
        if (!saved || saved.model !== model) writeJson(CART_KEY, { model: model, t: Date.now() });
      })
      .catch(function () {})
      .then(function () {
        resolveReady(current());
        announce();
      });
  }

  var cartTimer = null;
  function checkSoon() {
    clearTimeout(cartTimer);
    cartTimer = setTimeout(checkCart, 300);
  }

  window.kpVariantMatch = {
    get: current,
    ready: ready,
    refresh: checkSoon
  };

  // ---- Prices ----

  function withSeparators(value, decimals, thousands, decimal) {
    var parts = value.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  }

  function money(amount, currency) {
    var format = window.theme && window.theme.moneyFormat;
    var value = parseFloat(amount);
    if (isNaN(value)) return '';

    if (!format || !MONEY_TOKEN.test(format)) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: currency || 'GBP' }).format(value);
      } catch (e) {
        return value.toFixed(2);
      }
    }

    return format.replace(MONEY_TOKEN, function (match, type) {
      switch (type) {
        case 'amount_no_decimals':
          return withSeparators(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withSeparators(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withSeparators(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return withSeparators(value, 0, ' ', ',');
        case 'amount_with_space_separator':
          return withSeparators(value, 2, ' ', ',');
        default:
          return withSeparators(value, 2, ',', '.');
      }
    });
  }

  // ---- Asking the store for a model's variants ----

  var FRAGMENT =
    'fragment V on ProductVariant { id price { amount currencyCode } compareAtPrice { amount }' +
    ' image { s: url(transform: { maxWidth: 400 }) m: url(transform: { maxWidth: 700 }) l: url(transform: { maxWidth: 1100 }) } }';

  // Up to three variants per product: the model in each case type. "ignoreUnknownOptions"
  // lets a product with only a Model option (no case types) answer too.
  function fields() {
    return TYPES.map(function (type, i) {
      return 'v' + i + ': variantBySelectedOptions(selectedOptions: [{ name: "Model", value: $m }, { name: "Case Type", value: ' + JSON.stringify(type) + ' }], ignoreUnknownOptions: true) { ...V }';
    }).join(' ');
  }

  // { variant (Tough first), from (cheapest, only when the case types differ) }
  function summary(node) {
    if (!node) return { variant: null };
    var found = [node.v0, node.v1, node.v2].filter(Boolean);
    if (!found.length) return { variant: null };

    var ids = {};
    var low = null;
    found.forEach(function (v) {
      ids[v.id] = true;
      var amount = parseFloat(v.price.amount);
      if (!isNaN(amount) && (low === null || amount < low)) low = amount;
    });

    return { variant: found[0], from: Object.keys(ids).length > 1 ? low : null };
  }

  var cache = {};
  var inFlight = {};

  function ask(model, list, byHandle) {
    var parts = list.map(function (key, n) {
      return 'p' + n + ': ' + (byHandle ? 'product(handle: ' + JSON.stringify(key) + ')' : 'product(id: ' + JSON.stringify('gid://shopify/Product/' + key) + ')') + ' { ' + fields() + ' }';
    });
    var query = 'query ($m: String!, $c: CountryCode) @inContext(country: $c) { ' + parts.join(' ') + ' } ' + FRAGMENT;

    list.forEach(function (key) {
      inFlight[model + '|' + key] = true;
    });

    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: query, variables: { m: model, c: (window.Shopify && window.Shopify.country) || null } })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (json) {
        var data = (json && json.data) || {};
        list.forEach(function (key, n) {
          cache[model + '|' + key] = summary(data['p' + n]);
        });
      })
      .catch(function () {
        list.forEach(function (key) {
          cache[model + '|' + key] = { variant: null };
        });
      })
      .then(function () {
        list.forEach(function (key) {
          delete inFlight[model + '|' + key];
        });
        paintAll();
      });
  }

  // ---- Painting ----

  function setVariantInLinks(links, variant) {
    var number = variant.id.split('/').pop();
    links.forEach(function (link) {
      try {
        var url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.set('variant', number);
        link.setAttribute('href', url.pathname + url.search + url.hash);
      } catch (e) {}
    });
    return number;
  }

  // Theme product tiles (snippets/product-item.liquid).
  var tileKind = {
    find: function () {
      return document.querySelectorAll('.product-item[data-id]');
    },
    key: function (tile) {
      return tile.getAttribute('data-id');
    },
    remember: function (tile) {
      var img = tile.querySelector('img.image__img');
      return {
        src: img ? img.getAttribute('src') : null,
        srcset: img ? img.getAttribute('srcset') : null,
        variant: tile.getAttribute('data-variant-id'),
        links: Array.prototype.map.call(tile.querySelectorAll('a[href*="/products/"]'), function (a) {
          return [a, a.getAttribute('href')];
        }),
        prices: Array.prototype.map.call(tile.querySelectorAll('.product-item__price-main'), function (p) {
          return [p, p.innerHTML];
        })
      };
    },
    restore: function (tile, orig) {
      var img = tile.querySelector('img.image__img');
      if (img) {
        if (orig.srcset !== null) img.setAttribute('srcset', orig.srcset);
        if (orig.src !== null) img.setAttribute('src', orig.src);
      }
      orig.links.forEach(function (pair) {
        if (pair[1] !== null) pair[0].setAttribute('href', pair[1]);
      });
      orig.prices.forEach(function (pair) {
        pair[0].innerHTML = pair[1];
      });
      if (orig.variant !== null) tile.setAttribute('data-variant-id', orig.variant);
    },
    paint: function (tile, hit) {
      var v = hit.variant;
      var img = tile.querySelector('img.image__img');
      if (img && v.image) {
        img.setAttribute('srcset', v.image.s + ' 400w, ' + v.image.m + ' 700w, ' + v.image.l + ' 1100w');
        img.setAttribute('src', v.image.m);
      }

      var number = setVariantInLinks(Array.prototype.slice.call(tile.querySelectorAll('a[href*="/products/"]')), v);
      if (tile.hasAttribute('data-variant-id')) tile.setAttribute('data-variant-id', number);

      var html = '';
      if (hit.from !== null && hit.from !== undefined) {
        html = 'from <span>' + money(hit.from, v.price.currencyCode) + '</span>';
      } else {
        var price = parseFloat(v.price.amount);
        var compare = v.compareAtPrice ? parseFloat(v.compareAtPrice.amount) : 0;
        html = compare > price
          ? '<s class="t-subdued">' + money(compare, v.price.currencyCode) + '</s> <span class="sale">' + money(price, v.price.currencyCode) + '</span>'
          : '<span>' + money(price, v.price.currencyCode) + '</span>';
      }
      each(tile.querySelectorAll('.product-item__price-main'), function (el) {
        el.innerHTML = html;
      });
    }
  };

  // "You may also like" and "Recently viewed" cards (assets/kp-recs.js, assets/kp-recent.js).
  var cardKind = {
    byHandle: true,
    find: function () {
      return document.querySelectorAll('a.kp-recs__card');
    },
    key: function (card) {
      var match = (card.getAttribute('href') || '').match(/\/products\/([^\/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    },
    remember: function (card) {
      var img = card.querySelector('img');
      var price = card.querySelector('.kp-recs__price');
      return {
        href: card.getAttribute('href'),
        src: img ? img.getAttribute('src') : null,
        srcset: img ? img.getAttribute('srcset') : null,
        price: price ? price.innerHTML : null,
        priceClass: price ? price.className : null
      };
    },
    restore: function (card, orig) {
      var img = card.querySelector('img');
      var price = card.querySelector('.kp-recs__price');
      card.setAttribute('href', orig.href);
      if (img) {
        if (orig.srcset !== null) img.setAttribute('srcset', orig.srcset);
        if (orig.src !== null) img.setAttribute('src', orig.src);
      }
      if (price && orig.price !== null) {
        price.innerHTML = orig.price;
        price.className = orig.priceClass;
      }
    },
    paint: function (card, hit) {
      var v = hit.variant;
      var img = card.querySelector('img');
      var price = card.querySelector('.kp-recs__price');

      setVariantInLinks([card], v);

      if (img && v.image) {
        img.setAttribute('srcset', v.image.s + ' 400w, ' + v.image.m + ' 700w');
        img.setAttribute('src', v.image.s);
      }

      // A price only where the row showed one (Recently viewed leaves it out after a
      // currency change).
      if (!price || !price.textContent.trim()) return;

      var cur = v.price.currencyCode;
      if (hit.from !== null && hit.from !== undefined) {
        price.className = 'kp-recs__price';
        price.innerHTML = 'From ' + money(hit.from, cur);
      } else {
        var amount = parseFloat(v.price.amount);
        var was = v.compareAtPrice ? parseFloat(v.compareAtPrice.amount) : 0;
        price.className = 'kp-recs__price' + (was > amount ? ' kp-recs__price--sale' : '');
        price.innerHTML = money(amount, cur) + (was > amount ? ' <s>' + money(was, cur) + '</s>' : '');
      }
    }
  };

  function paintKind(kind, model) {
    var missing = [];

    each(kind.find(), function (el) {
      // Tiles on a page with the model dropdown belong to assets/kp-case-filters.js.
      if (kind === tileKind && document.querySelector('.kp-case-filters')) return;

      var key = kind.key(el);
      if (!key) return;
      if ((el.kpMatch || '') === model) return;

      if (!model) {
        if (el.kpMatchOrig) kind.restore(el, el.kpMatchOrig);
        el.kpMatch = '';
        return;
      }

      var hit = cache[model + '|' + key];
      if (!hit) {
        if (!inFlight[model + '|' + key] && missing.indexOf(key) < 0) missing.push(key);
        return;
      }

      if (!el.kpMatchOrig) el.kpMatchOrig = kind.remember(el);
      else if (el.kpMatch) kind.restore(el, el.kpMatchOrig);

      el.kpMatch = model;
      if (hit.variant) kind.paint(el, hit);
    });

    for (var i = 0; i < missing.length; i += BATCH) {
      ask(model, missing.slice(i, i + BATCH), !!kind.byHandle);
    }
  }

  function paintAll() {
    if (shownModel === null) return;
    paintKind(tileKind, shownModel);
    paintKind(cardKind, shownModel);
  }

  // ---- Product pages: open on the matched model ----

  // Only when the address does not already name a variant (a link from a tile painted
  // above already does). The theme's own Model select is set and its normal change
  // event fired, so the theme loads the variant and redraws its picker
  // (assets/kp-product-picker.js rebuilds on top of it), exactly as for a shopper's pick.
  function matchProductPage() {
    if (!/\/products\//.test(window.location.pathname)) return;
    if (new URLSearchParams(window.location.search).has('variant')) return;

    ready.then(function () {
      var model = current();
      if (!model) return;

      var tries = 0;
      (function attempt() {
        var select = null;
        each(document.querySelectorAll('.product__variants-wrapper select[name="options[Model]"]'), function (el) {
          if (!select && !el.closest('.quick-product-modal')) select = el;
        });

        if (!select) {
          if (++tries < 20) setTimeout(attempt, 150);
          return;
        }

        if (select.value === model) return;
        var has = Array.prototype.some.call(select.options, function (option) {
          return option.value === model;
        });
        if (!has) return;

        select.value = model;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      })();
    });
  }

  // ---- Start ----

  // New tiles and rows (infinite scroll, rows drawn as the shopper scrolls).
  function watch() {
    if (!window.MutationObserver) return;
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(paintAll, 80);
    }).observe(document.body, { childList: true, subtree: true });
  }

  [
    'rebuy:cart.change',
    'rebuy:cart.add',
    'rebuy:smartcart.line-item-removed',
    'kp:cart.change',
    'cart:updated'
  ].forEach(function (name) {
    document.addEventListener(name, checkSoon);
  });

  // A model chosen in the collection page dropdown (assets/kp-case-filters.js).
  document.addEventListener('kp:model-chosen', announce);

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) checkCart();
  });

  function start() {
    // Show what this visit already knows straight away, then check the cart.
    if (cartRecord() || choiceRecord()) announce();
    watch();
    checkCart();
    matchProductPage();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
